import { IconAlert, IconDigest, IconInfo, IconLock, IconRelease, IconShield } from '@developerz/ui';
import { A, useLocation } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { type EmailRouteCategoryMeta, EmailRouteRow } from '../components/EmailRouteRow';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Toast } from '../components/Toast';
import { t } from '../i18n';
import { resetEmailRoute, setEmailRoute, useEmailRoutes } from '../lib/emailRoutes';
import { orgFromPath, orgHref } from '../lib/org';
import { SESSION_TOKEN } from '../lib/session';
import { createToast, NOTICE_TTL_MS } from '../lib/toast';

// Escalation-category → email routing (#325). Per docs/idea/notifications.md the
// model is default + per-category override: every category is ALWAYS listed and
// routes to the account's primary email unless overridden, and `escalate.security`
// can never be muted. This view renders the resolved matrix from `/v1/email-routes`
// as a .row-list and drives set/reset. UI metadata (icon/cadence) lives here; the
// resolved destination comes from the API. `token` is injectable for tests
// (mirrors `AccountKeys`); the router mounts it with no props → dev session.
//
// This page is address ROUTING only. The per-channel preference grid + push
// controls moved to the dedicated Notifications page (slice 09 task 56); the two
// cross-link.

const CATEGORIES: readonly EmailRouteCategoryMeta[] = [
  {
    key: 'escalate.security',
    descKey: 'email_routes.desc_security',
    icon: IconLock,
    cadence: 'immediate',
    locked: true,
  },
  {
    key: 'escalate.license',
    descKey: 'email_routes.desc_license',
    icon: IconShield,
    cadence: 'immediate',
  },
  {
    key: 'escalate.hostile_tone',
    descKey: 'email_routes.desc_hostile',
    icon: IconAlert,
    cadence: 'immediate',
  },
  {
    key: 'ask_before_acting.*',
    descKey: 'email_routes.desc_ask',
    icon: IconInfo,
    cadence: 'immediate',
  },
  {
    key: 'satisfying.*',
    descKey: 'email_routes.desc_satisfying',
    icon: IconRelease,
    cadence: 'immediate',
  },
  {
    key: 'digest.weekly',
    descKey: 'email_routes.desc_digest',
    icon: IconDigest,
    cadence: 'weekly',
  },
];

export function EmailRoutes(props: { token?: string | null } = {}): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session" and must
  // survive, or the signed-out branch can't be driven from a dev build.
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const query = useEmailRoutes(token);
  const location = useLocation();
  // No token = prod pre-SSO: gate the whole route so set/reset controls (which
  // could only fire unauthenticated) never render. Mirrors AccountKeys.
  const hasSession = token !== null;

  // Saved/reset toast — lives on the container (not the row) so it survives the
  // post-mutation refetch, which remounts the row subtree. Auto-dismisses.
  const notice = createToast(NOTICE_TTL_MS);
  const flash = (msg: string): void => notice.notify(msg);

  const onSave = async (category: string, address: string): Promise<void> => {
    await setEmailRoute(category, address, token);
    await query.refetch();
    flash(t('email_routes.saved_notice'));
  };
  const onReset = async (category: string): Promise<void> => {
    await resetEmailRoute(category, token);
    await query.refetch();
    flash(t('email_routes.reset_notice'));
  };

  return (
    <section class="page email-routes">
      <PageHeader title={t('email_routes.title')} desc={t('email_routes.subtitle')} />

      <Show when={hasSession} fallback={<EmptyState message={t('email_routes.no_session')} />}>
        <QueryBoundary query={query} loading={<CardGridSkeleton listClass="email-routes__list" />}>
          {(page) => {
            const byCat = new Map(page.data.map((r) => [r.category, r] as const));
            return (
              <Panel title={t('email_routes.matrix_heading')}>
                <ul class="row-list">
                  <For each={CATEGORIES}>
                    {(meta) => (
                      <EmailRouteRow
                        meta={meta}
                        resolved={
                          byCat.get(meta.key) ?? {
                            category: meta.key,
                            destination: page.default_email,
                            is_override: false,
                          }
                        }
                        canEdit={hasSession}
                        onSave={onSave}
                        onReset={onReset}
                      />
                    )}
                  </For>
                </ul>
              </Panel>
            );
          }}
        </QueryBoundary>
        <p class="email-routes__link">
          {t('email_routes.notifications_lead')}{' '}
          <A href={orgHref('/account/notifications', orgFromPath(location.pathname))}>
            {t('email_routes.notifications_link')}
          </A>
        </p>
      </Show>

      <Toast message={notice.toast()?.message ?? null} onDismiss={notice.dismiss} />
    </section>
  );
}
